import lodashMerge from 'lodash.merge';
import { trackPermutiveCustomEvent } from '@gumtree/shared/src/util/permutive/track-event';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { Device } from '@gumtree/shared/src/types/client-data';
import {
    addCategoryToDataLayer,
    addLocationToDataLayer,
    updatePageTypeOnDataLayer,
} from '@gumtree/shared/src/util/data-layer-service';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { MetricsOptions } from '@gumtree/web-bff/src/data/common/fetch';
import httpMetrics from '@gumtree/web-bff/src/metrics/modules/http-metrics';
import { getGrowthbookClientSide } from '@gumtree/shell/src/growthbook/init-growthbook';
import {
    AutoRenewMetadata,
    FeatureType,
    PricingMetadata,
    SyiTrackingActions,
} from '@gumtree/shared/src/types/syi';
import { L1CategoryIds, L3CategoryIds } from '@gumtree/shared/src/types/categories';
import { getL2CategoryIdBySeoName } from '@gumtree/shared/src/util/category-mapping';
import { partition } from '@gumtree/shared/src/util/list-of-primitives';
import { toBooleanUnlessUndefined } from '@gumtree/shared/src/util/permutive/datalayer-parser';
import { AUTO_RENEW_TYPE } from '@gumtree/shared/src/types/auto-renew-feature';
import type { PostAdPageType } from '../get-data';
import type { SyiUseDispatch } from '../reducers';
import {
    generateTrackLogOnCookie,
    getPageStateThunk,
    isSyiAiPostWithAllCondition,
    MappedPanel,
    SyiModelStatus,
    updateSyi,
    updateSyiFormError,
} from '../reducers/syi';
import { CategoryChildrenData, CategoryList, CategoryListOption } from './category-list';
import { Form } from './form';
import {
    CategoryCrumb,
    computeListingDetails,
    LocationCrumb,
    PostAdImageDef,
    SyiModel,
    SyiModelAttrs,
} from './syi-model';
import CategorySuggestion from './category-suggestion';
import { deepSerializeForm } from './deep-serialize-form';
import ImageUpload from './image-upload';
import VrnLookup from '../components/panels/vehicle/vrn-lookup';
import { getPostAdPageType } from './post-ad-page-type-service';
import { canSeeMazumaOptions, MAZUMA_OFF_VARIANT } from './mazuma-utils';
import { canSeeCarWow } from './carwow-utils';
import { getServersideErrorsToDisplayErrorsFor } from './validation-service';
import { PanelName, PanelNameMapping } from '../components/all-components-definitions';

export type {
    PriceMetadataItem,
    PriceMetadataValue,
    PricingMetadata,
    FeatureOptions,
    AutoRenewMetadata,
} from '@gumtree/shared/src/types/syi';

export const transformPanels = (
    categoryCrumb: CategoryCrumb | undefined,
    panels: PanelName[],
    categoryId: number,
    dispatch: SyiUseDispatch
): PanelName[] => {
    const categoryL1Check =
        categoryCrumb && categoryCrumb.length > 1 ? categoryCrumb[0]?.id ?? '' : '';

    const categoryCrumbCheck =
        categoryCrumb && categoryCrumb.length > 1 && categoryCrumb[1] ? categoryCrumb[1].id : '';

    const isFreebies = (categoryId: number | string) =>
        getL2CategoryIdBySeoName('freebies') === Number(categoryId);

    const isServicesCategory = (categoryL1Check: number) =>
        categoryL1Check === L1CategoryIds.SERVICES;

    const isPetsCategory = (categoryCrumbCheck: number | string) =>
        getL2CategoryIdBySeoName('pets-for-sale') === Number(categoryCrumbCheck);

    const isPropertyCategory = (categoryId: number | string) => {
        const id = Number(categoryId);
        return (
            id === getL2CategoryIdBySeoName('property-to-rent') ||
            id === getL2CategoryIdBySeoName('property-to-share')
        );
    };

    const isAquariumCategory = (categoryId: number) =>
        [L3CategoryIds.AQUARIUM, L3CategoryIds.FISH].includes(categoryId);

    const [firstPanel, secondPanel, thirdPanel, ...fourthAndBeyondPanels] = panels.filter(
        (p) => !(isFreebies(categoryId) && p === PanelNameMapping.PRICE)
    );

    const clientSideOrderedPanels: PanelName[] = [
        PanelNameMapping.SELLER_TYPE,
        PanelNameMapping.IMAGES,
        PanelNameMapping.AD_TITLE,
        PanelNameMapping.VEHICLE_SPECIFICATIONS,
        PanelNameMapping.ADDITIONAL_FEATURES,
        PanelNameMapping.OVERALL_PRICE,
    ];

    const isAiPostWithAllCondition = dispatch(isSyiAiPostWithAllCondition());
    if (isAiPostWithAllCondition) {
        for (const panel of attributePanels) {
            clientSideOrderedPanels.push(panel);
        }
    }

    const [panelsClientSideOrdered, panelsNotClientSideOrdered] = partition(
        fourthAndBeyondPanels ?? [],
        (_) => clientSideOrderedPanels.includes(_)
    );

    return [
        firstPanel,
        ...(isPetsCategory(Number(categoryCrumbCheck)) ? [PanelNameMapping.PET_ADVICE_PANEL] : []),
        ...(isServicesCategory(Number(categoryL1Check)) ? [PanelNameMapping.SELLER_SKILLS] : []),
        ...(isPropertyCategory(categoryId) ? [PanelNameMapping.PROPERTY_DETAILS] : []),
        ...(isAquariumCategory(categoryId) ? [PanelNameMapping.AQUARIUM_DETAILS] : []),
        ...panelsClientSideOrdered.filter((_) => _ === PanelNameMapping.SELLER_TYPE),
        /**
         * If vrn param is passed, this will be Location, and we want to make sure the price advice box in sellerType goes above
         * When edit ad, this will be AdTitle, which we want to make sure it goes undernearth Images
         *
         * 🚧 This is a temporary hack. Ideally, this order changes should happened in the API we fetch from.
         *    We should consider to amend this in the API when we productionise SYI
         */
        ...(thirdPanel && thirdPanel !== PanelNameMapping.AD_TITLE ? [thirdPanel] : []),
        ...panelsClientSideOrdered.filter((_) => _ === PanelNameMapping.IMAGES),
        ...panelsClientSideOrdered.filter((_: PanelName) => attributePanels.includes(_)),
        ...(thirdPanel === PanelNameMapping.AD_TITLE ? [thirdPanel] : []),
        /**
         * Normally, it should be the locationPanel
         * It'll be the vehicleSpecPanel if vrn param is passed, and we want to make sure the price advice box in sellerType goes above
         */
        secondPanel,
        ...panelsClientSideOrdered.filter((_) => _ === PanelNameMapping.VEHICLE_SPECIFICATIONS),
        ...panelsClientSideOrdered.filter((_) => _ === PanelNameMapping.ADDITIONAL_FEATURES),
        ...panelsClientSideOrdered.filter((_) => _ === PanelNameMapping.AD_TITLE),
        ...panelsNotClientSideOrdered,
        // ...preConfirmPanel,
    ];
};

export const attributePanels: PanelName[] = [
    PanelNameMapping.BRANDS,
    PanelNameMapping.ATTRIBUTE_PANEL,
    PanelNameMapping.OPTIONAL_PANEL,
];

export class DtlSyi {
    allowTracking = false;
    formName = 'syi' as const;
    model!: SyiModel;
    options = { draftEnabled: true };
    submittedLogin = false;

    /** 0-based index in `[Motors, For Sale, Property, Jobs, Services, Community, Pets]` */
    catL1Index = null as null | number;
    categoryLists: CategoryList[];
    categorySuggestion!: CategorySuggestion;

    locL1Index = null as null | number;
    locationLists: CategoryList[];

    imageUpload: ImageUpload;
    vrnLookup: VrnLookup;

    public postAdPageType!: string;
    public postadURL!: string;
    public initialFetchURL!: string;
    public saveFetchURL!: string;
    /** Defined for SYI, not on Category Page */
    public form?: Form;
    public dispatch!: SyiUseDispatch;
    public bffBumpUpBaseUrl!: string;
    public sellerBaseUrl!: string;
    public sellerUrl!: string;
    public bffPhoneVerificationUrl!: string;
    public device!: Device;
    public accountId!: number;
    public isOnAiCollectPage!: boolean;

    get categoryContinueButton(): HTMLButtonElement {
        return document.querySelector('button#continueButton')!;
    }
    get locationContinueButton(): HTMLButtonElement {
        return document.querySelector('a#locationIdBtn')!;
    }
    get hiddenCategoryInput(): HTMLInputElement {
        return document.querySelector('#hidden-category-id')!;
    }
    get hiddenLocationInput(): HTMLInputElement {
        return document.querySelector('#hidden-location-id')!;
    }
    get rootFormDiv() {
        return document.querySelector('form > div.syi-panels-container') as HTMLDivElement;
    }

    constructor() {
        this.categoryLists = [0, 1, 2, 3].map(
            (i) => new CategoryList(() => this.categoryLists, i, 'category')
        );
        this.locationLists = [0, 1, 2, 3].map(
            (i) => new CategoryList(() => this.locationLists, i, 'location')
        );
        this.imageUpload = new ImageUpload();
        this.vrnLookup = new VrnLookup();
    }

    async init(
        postAdPageType: PostAdPageType,
        postadURL: string,
        initialFetchURL: string,
        saveFetchURL: string,
        dispatch: SyiUseDispatch,
        bffBumpUpBaseUrl: string,
        sellerBaseUrl: string,
        accountId: number,
        sellerUrl: string,
        bffPhoneVerificationUrl: string,
        device: Device,
        isOnAiCollectPage: boolean,
        bffUrl: string
    ) {
        this.postAdPageType = postAdPageType;
        this.postadURL = postadURL;
        this.initialFetchURL = initialFetchURL;
        this.saveFetchURL = saveFetchURL;
        this.dispatch = dispatch;
        this.sellerUrl = sellerUrl;
        this.bffBumpUpBaseUrl = bffBumpUpBaseUrl;
        this.sellerBaseUrl = sellerBaseUrl;
        this.bffPhoneVerificationUrl = bffPhoneVerificationUrl;
        this.device = device;
        this.accountId = accountId;
        this.isOnAiCollectPage = isOnAiCollectPage;

        if (document.getElementById('syi-form')) {
            this.form = new Form(
                {
                    clear: true, // From attrs data-clear="true" of <form>
                    disableEnter: true,
                    jsvalidate: true,
                    radioSubmit: true,
                    radioSubmitChannel: 'syi-dtl.submit',
                },
                this.dispatch
            );
            this.form.init();

            // triggered on change form element e.g. description
            this.rootFormDiv.addEventListener('change', async (e) => {
                const response = postAdPageType === 'create' ? await this.submitDraft() : undefined;

                try {
                    const el = e.target as HTMLFormElement;
                    const priceStr = response?.form.attributes.price;
                    const validEl = el.closest('.panel')?.classList.contains('is-valid');
                    const invalidEl = el.closest('.panel')?.classList.contains('is-invalid');

                    // update tracking if features checkbox
                    const regexPattern = /^features\[\w+\]\[selected\]$/;
                    let changedElementString;

                    if (regexPattern.test(el.name) && el.type === 'checkbox') {
                        if (el.checked) {
                            changedElementString = el.name;
                        } else {
                            changedElementString = el.name.replace(/\[selected\]$/, '[unselected]');
                        }
                    } else {
                        changedElementString = el.name;
                    }

                    el.name &&
                        el.name !== 'attributes[dog_breed]' &&
                        el.name !== 'attributes[cat_breed]' &&
                        trackGA4Event<GA4.PostAdInteractionEvent>({
                            event: 'ad_posted_form_interactions',
                            formName: 'syi-form',
                            formStep: `change ${changedElementString}`,
                            formValidation:
                                validEl === true
                                    ? 'success'
                                    : invalidEl === true
                                    ? 'failure'
                                    : undefined,
                            adImagesUploading:
                                el.name === 'images-file-input'
                                    ? (e.target as HTMLInputElement).files?.length ?? 0
                                    : undefined,
                            adPriceAdded:
                                el.name === 'attributes[price]' && priceStr
                                    ? Number(priceStr)
                                    : undefined,
                            ...computeListingDetails(),
                        });
                } catch (e) {
                    console.error('dtl-syi rootFormDiv.change', e);
                }
            });
        }

        this.categorySuggestion = new CategorySuggestion(dispatch);
        this.model = new SyiModel(
            postadURL,
            initialFetchURL,
            saveFetchURL,
            bffBumpUpBaseUrl,
            sellerBaseUrl,
            accountId,
            sellerUrl,
            bffPhoneVerificationUrl,
            device,
            isOnAiCollectPage,
            bffUrl
        );
        if (this.form) {
            await this.model.initialFetch();
            trackV2('PostAdCategory');
            if (this.postAdPageType === 'category' && !this.isOnAiCollectPage) {
                // photo first don't need init draft
                return;
            }
            const imageDefs = (this.model.attrs.postAdImages ?? []).slice();
            this.dispatch(updateSyi({ imageDefs }));
        }
    }

    disableContinueButton(type) {
        if (type === 'category') {
            this.categoryContinueButton?.setAttribute('disabled', 'true');
            this.categoryContinueButton?.classList.add('is-disabled');
        } else if (type === 'location') {
            this.locationContinueButton?.setAttribute('disabled', 'true');
            this.locationContinueButton?.classList.add('is-disabled');
        }
    }

    enableContinueButton(type) {
        if (type === 'category') {
            this.categoryContinueButton.removeAttribute('disabled');
            this.categoryContinueButton.classList.remove('is-disabled');
        } else if (type === 'location') {
            this.locationContinueButton.removeAttribute('disabled');
            this.locationContinueButton.classList.remove('is-disabled');
        }
    }

    getBaseTrackingData(formJson: Form['formJson']) {
        return {
            status: this.model.attrs.status,
            totalPrice: this.model.attrs.totalPrice,
            isSubmitForm: formJson.submitForm as boolean,
            sellerType: formJson.attributes
                ? (formJson.attributes as Record<string, any>).seller_type
                : undefined,
            allowTracking: this.allowTracking,
            offerToDealerValue: formJson.attributes
                ? (formJson.attributes as Record<string, any>).offer_to_dealer
                : undefined,
            reservePrice: formJson.attributes
                ? (formJson.attributes as Record<string, any>).reserve_price
                : undefined,
            price: formJson.attributes
                ? (formJson.attributes as Record<string, any>).price
                : undefined,
            carListingOptions: formJson.attributes
                ? (formJson.attributes as Record<string, any>).car_listing_options
                : undefined,
            petsAttributes: {
                dogBreed: formJson.attributes
                    ? (formJson.attributes as Record<string, any>).dog_breed
                    : undefined,
                catBreed: formJson.attributes
                    ? (formJson.attributes as Record<string, any>).cat_breed
                    : undefined,
                isDeflead: formJson.attributes
                    ? (formJson.attributes as Record<string, any>).deflead
                    : undefined,
                isVaccinated: formJson.attributes
                    ? (formJson.attributes as Record<string, any>).vaccinated
                    : undefined,
                isMicrochipped: formJson.attributes
                    ? (formJson.attributes as Record<string, any>).microchipped
                    : undefined,
                isNeuteredOrSpayed: formJson.attributes
                    ? (formJson.attributes as Record<string, any>).neutered_or_spayed
                    : undefined,
            },
        };
    }

    /**
     * Originally CategoryList.getCategoryTree
     */
    async loadLocationLists() {
        const id = this.hiddenLocationInput.value;
        const { baseConfig } = this.dispatch(getPageStateThunk());
        const url = `${baseConfig.buyerUrl}/ajax/location/dropdown?${new URLSearchParams({
            id: `${id}`,
        })}`;
        const resp = await fetchOrThrow(url, undefined, {
            serviceName: 'buyer',
            path: '/ajax/location/dropdown',
            method: 'GET',
        });

        let { locations: catData } = (await resp.json()) as { locations: CategoryChildrenData[] };
        let drilledData = undefined as undefined | CategoryChildrenData;

        this.locationLists.forEach((x) => (x.options.initial = undefined));

        this.locL1Index =
            {
                England: 0,
                Scotland: 1,
                Wales: 2,
                'Northern Ireland': 3,
            }[catData.find((x) => x.drilled)?.name ?? ''] ?? null;

        for (const catList of this.locationLists) {
            drilledData = catData.find((x) => x.drilled);
            if (drilledData) {
                catList.categoryLevel = {
                    listings: drilledData,
                    selected: catList.options.selected, // ?
                    initial: catList.options.initial,
                    allLocAllowed: catList.options.anyloc ? true : false,
                    listIndex: catList.listIndex,
                };
                catList.specified = false; // Fixes clear
                // Final category is 'selected', ancestors are 'drilled'
                catList.selectedCategoryId =
                    drilledData.childrenItems.find((x) => x.drilled || x.selected)?.id ?? null;
                catData = drilledData.childrenItems;
            } else {
                break;
            }
        }
        dtlSyi.renderComponent();
    }

    /**
     * Load the templates
     */
    async loadTemplates() {
        const {
            panels,
            form: { categoryId },
            categoryCrumb,
        } = this.model.attrs;

        const extendedPanels = transformPanels(
            categoryCrumb,
            panels as PanelName[],
            categoryId,
            this.dispatch
        );
        let mappedPanels: MappedPanel[] = extendedPanels.map((panelName) => {
            return {
                name: panelName,
                data: lodashMerge({}, this.model.attrs, { panelName }),
            };
        });

        const pageState = this.dispatch(getPageStateThunk());
        const syiState = pageState.syi;

        const mazumaFeatureValue =
            !!getGrowthbookClientSide() &&
            getGrowthbookClientSide().getFeatureValue('GTP-3562', MAZUMA_OFF_VARIANT);

        const canViewCarListingOptions = canSeeCarWow(
            window,
            pageState.userType,
            this.postAdPageType,
            this.model.attrs.sellerType?.sellerType,
            categoryCrumb?.[1]?.id
        );

        const showMazumaOptions = canSeeMazumaOptions(
            mazumaFeatureValue,
            this.isOnAiCollectPage,
            this.postAdPageType as PostAdPageType,
            categoryCrumb?.[2]?.id,
            categoryCrumb?.[3]?.id
        );

        if (canViewCarListingOptions && syiState.selectedListingType !== 'regular') {
            const sellerTypePanelIndex = mappedPanels.findIndex(
                (panel) => panel.name === 'seller-type'
            );

            // When the seller type hasn't been selected, we don't want to display any other panels.
            if (sellerTypePanelIndex >= 0) {
                mappedPanels = mappedPanels.slice(0, sellerTypePanelIndex + 1);
            }
        }

        if (showMazumaOptions && syiState.selectedListingType !== 'regular') {
            mappedPanels = mappedPanels.slice(0, 1);
        }

        this.dispatch(updateSyi({ mappedPanels, remountedAt: Date.now() }));
    }

    /**
     * Invoked every time we remount e.g. when click a feature.
     */
    async postLoadTemplates(mappedPanels: MappedPanel[]) {
        const categoryCrumb = mappedPanels.find((x) => x.name === 'category')?.data.categoryCrumb;
        dtlSyi.initCategoryLists('category', categoryCrumb);

        const locationCrumb = mappedPanels.find((x) => x.name === 'location')?.data.locationCrumb;
        dtlSyi.initCategoryLists('location', locationCrumb);

        if (locationCrumb && Object.keys(locationCrumb).length > 0) {
            this.dispatch(updateSyi({ hasSavedLocation: true }));
        }

        if (categoryCrumb && categoryCrumb.length > 0) {
            this.dispatch(
                updateSyi({
                    categorySeoNames: categoryCrumb.map((x) => x.seoName),
                    l1Category: categoryCrumb[0].seoName,
                })
            );
        }

        // this pause fixes e.g. 1st category change
        await new Promise((resolve) => setTimeout(resolve));

        this.form!.loadElements();
    }

    initCategoryLists(
        type: 'category' | 'location',
        categoryCrumb?: SyiModel['attrs']['categoryCrumb'] | LocationCrumb
    ) {
        const lvl1 = categoryCrumb?.[0]?.id;
        const lvl2 = categoryCrumb?.[1]?.id;
        const lvl3 = categoryCrumb?.[2]?.id;
        const lvl4 = categoryCrumb?.[3]?.id;
        const lvl5 = categoryCrumb?.[4]?.id;

        const options: CategoryListOption[] = [
            {
                channel: 'cat-lvl-2',
                trigger: 'category-l1',
                forward: 'cat-lvl-3',
                field: 'categoryId',
                button: 'continueButton',
                ...(lvl1 && { initial: lvl1 }),
                ...(lvl2 && { selected: lvl2 }),
            },
            {
                channel: 'cat-lvl-3',
                forward: 'cat-lvl-4',
                field: 'categoryId',
                button: 'continueButton',
                ...(lvl2 && { initial: lvl2 }),
                ...(lvl3 && { selected: lvl3 }),
            },
            {
                channel: 'cat-lvl-4',
                forward: 'cat-lvl-5',
                field: 'categoryId',
                button: 'continueButton',
                ...(lvl3 && { initial: lvl3 }),
                ...(lvl4 && { selected: lvl4 }),
            },
            {
                channel: 'cat-lvl-5',
                field: 'categoryId',
                button: 'continueButton',
                ...(lvl4 && { initial: lvl4 }),
                ...(lvl5 && { selected: lvl5 }),
            },
        ];

        switch (type) {
            case 'category':
                // 🚧 by category id instead?
                this.catL1Index =
                    {
                        Motors: 0,
                        'For Sale': 1,
                        Property: 2,
                        Jobs: 3,
                        Services: 4,
                        Community: 5,
                        Pets: 6,
                    }[categoryCrumb?.[0]?.name ?? ''] ?? null;

                this.categoryLists.forEach((catList, i) => catList.init(options[i], this.dispatch));
                break;
            case 'location':
                this.locationLists.forEach((catList, i) =>
                    catList.init({ ...options[i], clearField: 'postcode' }, this.dispatch)
                );
                break;
        }
    }

    /**
     * Change the given status of SYI from BE model
     * @function
     */
    statusChange(data?) {
        const attrs = this.model.attrs;
        const repost = this.postAdPageType === 'repost' && attrs.advertId;
        const edit = !repost && attrs.advertId;
        const categoryId = data ? parseInt(data.categoryId, 10) : undefined;
        const categoryChange = categoryId && categoryId !== attrs.form.categoryId;
        const locationId = data ? parseInt(data.locationId, 10) : undefined;
        const locationSelect = locationId && locationId !== attrs.form.locationId;
        const locationUpdate = data && data.postcode && data.postcode !== attrs.form.postcode;
        const selleTypeProp = 'seller_type';
        const sellerType = data && data.attributes && data.attributes[selleTypeProp];
        const sellerTypeChange = sellerType && sellerType !== attrs.form.attributes[selleTypeProp];

        let freePostAd: boolean;

        if (repost) {
            this.model.attrs.status = 'REPOST';
        }

        if (edit) {
            this.model.attrs.status = 'EDIT';
        }

        if (categoryChange) {
            this.model.attrs.status = 'CATEGORY';
        }

        if (locationSelect) {
            this.model.attrs.status = 'LOCATION';
        }

        if (locationUpdate) {
            this.model.attrs.status = 'LOCATION_UPDATE';
        }

        if (sellerTypeChange) {
            this.model.attrs.status = 'SELLER';
        }

        // attrs.status would always be 'FORM', will edit it here depends on its insertionPrice
        if (attrs.status?.includes('FORM')) {
            freePostAd = attrs.pricingMetadata?.insertionPrice?.price === 0;
            this.model.attrs.status = freePostAd ? 'FORM_FREE' : 'FORM_PAID';
        }

        this.allowTracking =
            !!data && !data.submitForm
                ? // !/FORM|EDIT|REPOST/g.test(attrs.status)
                  !(['EDIT', 'REPOST', 'FORM_FREE', 'FORM_PAID'] as SyiModelStatus[]).includes(
                      attrs.status
                  )
                : true;
    }

    /**
     * Clean draft and starts a new ad
     */
    async removeDraft() {
        try {
            const resp = await fetchOrThrow(
                this.postadURL,
                {
                    method: 'POST',
                    headers: { 'content-type': 'application/json' },
                    body: JSON.stringify({ removeDraft: true }),
                    credentials: 'include',
                },
                {
                    serviceName: 'seller',
                    path: '/postad',
                    method: 'POST',
                }
            );

            // reset model data
            const data = await resp.json();
            this.model.resetData(data.form ?? {});

            // reset requestId
            this.dispatch(generateTrackLogOnCookie(true));
        } catch (e) {
            this.model.handleError(e);
        }
    }

    /**
     * Used to Submit to the postad-draft API on input changes
     */
    async submitDraft(isOnAiFlow?: boolean) {
        if (!this.options.draftEnabled) {
            return; //bailout
        }

        const formDataJson = deepSerializeForm(this.form!.el);
        if (isOnAiFlow) {
            formDataJson.attributeStep = true;
        }
        return await this.model.save(formDataJson, 'saveDraft');
    }

    submitLogin(data: { loginNow: boolean }) {
        this.submittedLogin = data.loginNow;
    }

    /**
     * Used to Update the seller-skills
     */
    async updateSellerSkills(categoryId: number) {
        this.model.updateSellerSkills(categoryId);
    }

    /**
     * Used to Submit the seller-skills
     */
    async submitSellerSkills(isEdit: boolean, data) {
        const response = isEdit
            ? await this.model.editSellerSkills(data)
            : await this.model.createSellerSkills(data);

        if (!response || !response.success) {
            return;
        }
        const MAX_SELECTED_SKILLS = 40;
        const skills = response.data?.skills?.map(Number) || [];
        const limitedSkills = skills.slice(0, MAX_SELECTED_SKILLS);
        this.dispatch(
            updateSyi({
                isEditedSkills: true,
                accountCategorySkills: limitedSkills,
            })
        );
    }

    /**
     * Use cases include:
     * - Category component Continue
     * - Location component Continue
     * - Submitting the whole form (submitForm `true`)
     */
    async submitSYIForm(formJson: Form['formJson'], saveTypeParam) {
        if (this.isOnAiCollectPage) {
            formJson.attributeStep = true;
        } else if (saveTypeParam === 'saveAttrsOnDraft') {
            formJson.submitForm = false;
        } else {
            formJson.submitForm = formJson.submitForm !== false;
        }

        // Gum.RADIO.trigger('form.syi.groups.clear');
        this.form!.validationGroup = {};
        this.statusChange(formJson);

        const trackingData = this.getBaseTrackingData(formJson);
        const offerToDealerValueLookup = { Y: 'True', N: 'False' };

        if (
            SyiTrackingActions[trackingData.status] === SyiTrackingActions.FORM_FREE ||
            SyiTrackingActions[trackingData.status] === SyiTrackingActions.FORM_PAID
        ) {
            trackPermutiveCustomEvent(window, SyiTrackingActions[trackingData.status], {
                ad: { sellerType: trackingData.sellerType },
                pets: {
                    dogBreed: trackingData.petsAttributes.dogBreed as string | undefined,
                    catBreed: trackingData.petsAttributes.catBreed as string | undefined,
                    isDeflead: toBooleanUnlessUndefined(
                        trackingData.petsAttributes.isDeflead as 'Y' | 'N' | undefined
                    ),
                    isVaccinated: toBooleanUnlessUndefined(
                        trackingData.petsAttributes.isVaccinated as 'Y' | 'N' | undefined
                    ),
                    isMicrochipped: toBooleanUnlessUndefined(
                        trackingData.petsAttributes.isMicrochipped as 'Y' | 'N' | undefined
                    ),
                    isNeuteredOrSpayed: toBooleanUnlessUndefined(
                        trackingData.petsAttributes.isNeuteredOrSpayed as 'Y' | 'N' | undefined
                    ),
                },
            });

            if (trackingData.offerToDealerValue) {
                trackV2(
                    'OfferToDealerSelected',
                    offerToDealerValueLookup[trackingData.offerToDealerValue]
                );

                trackingData.reservePrice &&
                    trackV2(
                        'OTDReservePrice',
                        `P:${trackingData.price}/R:${trackingData.reservePrice}`
                    );
            }

            if (trackingData.carListingOptions) {
                trackV2('OTDSellTypeSelected', `${trackingData.carListingOptions}`);
            }
        }

        // Only feature tracking when form submit
        if (formJson.submitForm) {
            this.trackFeatures(formJson);
        }

        const saveType =
            saveTypeParam ?? (this.isOnAiCollectPage ? 'saveOnAttributeStep' : 'submitForm');
        const response = (await this.model.save(formJson, saveType))!;

        const filteredErrors = getServersideErrorsToDisplayErrorsFor(response?.form?.formErrors);

        if (Object.keys(filteredErrors).length > 0) {
            Object.keys(filteredErrors).forEach((fieldKey) => {
                this.dispatch(
                    updateSyiFormError({
                        elName: fieldKey,
                        errors: filteredErrors[fieldKey],
                        shouldShow: true,
                    })
                );
            });

            this.form?.markAsInvalid();
        }

        const { categoryCrumb, locationCrumb, pricingMetadata } = response;

        addCategoryToDataLayer(window.dataLayer, categoryCrumb!);
        addLocationToDataLayer(window.dataLayer, locationCrumb!);
        const postAdPageType = getPostAdPageType(pricingMetadata!);
        updatePageTypeOnDataLayer(window.dataLayer, postAdPageType);

        this.triggerTracking(trackingData, [
            'CATEGORY',
            'EDIT',
            'REPOST',
            'LOCATION',
            'LOCATION_UPDATE',
            'SELLER',
        ]);

        if (response.gaEvents) {
            response.gaEvents.forEach(
                (event) => event && trackV2(event.event, event.eventLabel, event)
            );
        }

        if (this.submittedLogin) {
            trackV2('BtnContinue');
            // window.location.href = `/login?cb=${this.model.initialFetchUrl}`;
            window.location.href = `/login?cb=${location.href}`;
        }

        return response as BaseSyiResponse;
    }

    /**
     * Query title recommendations
     */
    async getTitleSuggestions(input: string): Promise<string[]> {
        if (input.length === 0) {
            return [];
        }

        const result = await this.model.getTitleSuggestions(input);
        return result;
    }

    /**
     * Query category recommendations
     */
    async getCategorySuggestions(input: string, type: AIsuggestType) {
        if (input.length === 0 || !type) {
            return [];
        }

        const result = await this.model.getCategorySuggestions(input, type);
        return result;
    }

    /**
     * Query attributes recommendations
     */
    async getFormAttributesSuggestions(
        input: string,
        secInput: string | undefined,
        type: string | undefined,
        categoryName: string
    ) {
        if (input.length === 0 || !type) {
            return;
        }
        const result = await this.model.getFormAttributesSuggestions(
            input,
            secInput ?? '',
            type ?? '',
            categoryName
        );
        return result;
    }

    async getTitleAndDescSuggestions(param) {
        return this.model.fetchSuggestionTitleAndDesc(param);
    }

    mapFeatureName(feature: string, data) {
        switch (feature) {
            case 'WEBSITE_URL': {
                return 'WEB_URL' as const;
            }
            case 'FEATURED': {
                const featuredNameMap = {
                    FEATURE_3_DAY: 'TOP_AD_3',
                    FEATURE_7_DAY: 'TOP_AD_7',
                    FEATURE_14_DAY: 'TOP_AD_14',
                } as const;
                const dtlHelpers = helperFactory({ form: data });
                const featuredName = dtlHelpers.getFeatureProductName(feature);
                return featuredNameMap[featuredName as keyof typeof featuredNameMap];
            }
            default: {
                return feature;
            }
        }
    }

    trackFeatures(data) {
        const dtlHelpers = helperFactory({ form: data });
        const features = ['URGENT', 'FEATURED', 'WEBSITE_URL', 'SPOTLIGHT'] as const;
        const trackingLabel = features
            .filter((feature) => dtlHelpers.hasFeature(feature))
            .reduce((acc, feature, idx) => {
                const ftrName = `ftr${idx + 1}`;
                const mappedFeatureName = this.mapFeatureName(feature, data);
                return Object.assign({}, acc, { [ftrName]: mappedFeatureName });
            }, {} as Record<string, string>);

        // Only track features if label present
        if (Object.keys(trackingLabel).length !== 0) {
            // TODO 🚧 verify
            trackV2('FeatureAdBegin', undefined, {
                ...trackingLabel,
                eventValue: this.model.attrs.totalPrice ?? 0 * 100,
            });
        }
    }

    /**
     * Tracking events for SYI (dataLayer)
     */
    triggerTracking(
        {
            status,
            totalPrice = 0,
            isSubmitForm,
            sellerType,
            allowTracking,
        }: {
            status: SyiModelStatus;
            totalPrice: number | undefined;
            isSubmitForm: boolean;
            sellerType: any;
            allowTracking: boolean;
            offerToDealerValue: any;
        },
        permitEvents: (keyof typeof SyiTrackingActions)[]
    ) {
        if (!permitEvents.includes(status)) {
            return;
        }

        let eventValue: string | undefined;
        if (SyiTrackingActions[status] === 'PostAdPaidAttempt') {
            eventValue = (totalPrice * 100).toFixed();
        } else if (SyiTrackingActions[status] === 'PostAdFreeAttempt') {
            eventValue = (totalPrice * 100).toFixed();
        }

        const tracking = {
            event: SyiTrackingActions[status],
            eventLabel: undefined as undefined | string,
            eventValue,
        };

        // PostAdCategory Event is already emitted on Form Validation
        if (status === 'CATEGORY' && isSubmitForm) {
            return;
        }

        if (status === 'SELLER') {
            tracking.eventLabel = `sellerType=${sellerType};`;
        } else if (status === 'CATEGORY') {
            tracking.eventLabel = window.Gum.domain.tracking.categorySearchMethod;
        }

        if (!allowTracking) {
            return;
        }

        trackV2(tracking.event, tracking.eventLabel, {
            eventValue: tracking.eventValue,
        });
    }

    renderComponent() {
        this.dispatch(updateSyi({ updateCatsAt: Date.now() }));
    }
}

/** Single instance of above class */
export const dtlSyi = new DtlSyi();

/**
 * Helper functions for our templates
 */
export function helperFactory(obj: {
    form: PanelFormData;
    pricingMetadata?: BaseSyiResponse['pricingMetadata'];
    supportsPostToAnyLocation?: boolean;
}) {
    return {
        // Return a form value
        get(field) {
            const value = obj.form[field];
            // React automatically escapes values in JSX attributes, no manual escaping needed
            return value;
        },
        isEven(n) {
            return n % 2 === 0;
        },
        // Works out if a field should care about the error messages
        isPristine(field, attributes?) {
            return attributes ? !obj.form.attributes[field] : !obj.form[field];
        },
        // Works out if a field is valid
        isValid(field) {
            return this.isPristine(field) || !obj.form.formErrors[field];
        },
        // Works out if a field is valid
        isValidAttr(field) {
            return this.isPristine(field, true) || !obj.form.formErrors[`attributes['${field}']`];
        },
        // Returns the error message
        printError(field) {
            return obj.form.formErrors[field] ? obj.form.formErrors[field][0] : undefined;
        },
        // Returns the error message
        printErrorAttr(field) {
            const errField = `attributes['${field}']`;
            return obj.form.formErrors[errField] ? obj.form.formErrors[errField][0] : undefined;
        },
        // Return true is the feature is active
        hasFeature(feature: FeatureType) {
            return obj.form.features?.[feature]?.selected; // 🚧 saw this missing on staging
        },
        // Returns name of the feature
        getFeatureProductName(feature) {
            return obj.form.features[feature].productName;
        },
        // Return checked if feature is active
        setFeatureChecked(feature) {
            return this.hasFeature(feature) ? 'checked' : '';
        },
        // Returns a string of the category price
        getFeaturePrice(feature) {
            const featPrice = obj.pricingMetadata!.featureOptions[feature].prices;
            return featPrice ? featPrice[0].displayValue : false;
        },
        getPanelAttrFields(attributes: any[]) {
            const fields = [] as string[];
            for (let p = 0; p < attributes.length; p++) {
                fields.push(attributes[p].id);
            }
            return fields;
        },
        getSupportsPostToAnyLocation() {
            return obj.supportsPostToAnyLocation;
        },
    };
}

export async function fetchOrThrow(
    input: RequestInfo | URL,
    init?: RequestInit | undefined,
    metricsTaggingOptions?: MetricsOptions
) {
    try {
        const startDateTime = new Date().getTime();

        const resp = await fetch(input, init);

        const endDateTime = new Date().getTime();

        if (metricsTaggingOptions) {
            const pageState = dtlSyi.dispatch(getPageStateThunk());

            httpMetrics.registerHttpEvent(
                metricsTaggingOptions.serviceName,
                metricsTaggingOptions.method,
                metricsTaggingOptions.path,
                resp.status,
                endDateTime - startDateTime,
                {
                    appViewDevice: pageState.page.appViewDevice,
                }
            );
        }

        if (!resp.ok) {
            throw new FetchError(`fetch not ok: ${resp.status}`, resp.status);
        }
        return resp;
    } catch (e) {
        console.error('fetch error', e);
        throw e;
    }
}

export class FetchError extends Error {
    constructor(message: string, public status: number) {
        super(message);
    }
}

export function extractParsedDataAttrs(el: HTMLElement, attrPrefix = 'data-') {
    return el
        .getAttributeNames()
        .filter((x) => x.startsWith(attrPrefix))
        .reduce(
            (agg, attributeName) => ({
                ...agg,
                [attributeName.slice(attrPrefix.length)]: parseJsonOrString(
                    el.getAttribute(attributeName)!
                ),
            }),
            {} as Record<string, string>
        );
}

export function parseJsonOrString(input: string) {
    try {
        return JSON.parse(input);
    } catch {
        return input;
    }
}

export interface BaseSyiResponse {
    advertId?: number | string;
    categoryId?: number;
    categoryCrumb?: SyiModelAttrs['categoryCrumb'];
    locationCrumb?: SyiModelAttrs['locationCrumb'];
    form: PanelFormData;
    gaEvents?: any;
    Helpers: typeof helperFactory;
    jobsConfig: {
        postAdUrl: string;
        url: string;
    };
    pricingMetadata: null | PricingMetadata;
    autoRenewMetadata: null | AutoRenewMetadata;
    redirectTo?: string;
    sellerType: null | SyiSellerType;
    supportsChangeCategory: boolean;
    supportsChangeLocation: null | boolean;
    supportsPostToAnyLocation: boolean;
    userLoggedIn?: boolean;
    postAdImages?: PostAdImageDef[];
    panels?: string[];
}

export type AttributeKey =
    | 'additional_features_available'
    | 'adjustable_steering_wheel'
    | 'air_conditioning'
    | 'airbag_knee_driver'
    | 'alarm_system_remote_anti_theft'
    | 'alloy_wheels'
    | 'android_auto'
    | 'anti_lock_braking'
    | 'apple_car_play'
    | 'automatic_air_climate_control'
    | 'automatic_headlights_with_dusk_sensor'
    | 'automatic_stop_start'
    | 'aux_usb_input_socket'
    | 'bluetooth_connectivity'
    | 'central_locking_remote_control'
    | 'child_proof_rear_door_locks'
    | 'cruise_control'
    | 'curtain_airbags'
    | 'daytime_running_lights'
    | 'dual_front_airbags_package'
    | 'electronic_stability_program'
    | 'engine_immobiliser'
    | 'heated_door_mirrors'
    | 'heated_seats'
    | 'leather_seats'
    | 'leather_steering_wheel'
    | 'led_headlights'
    | 'multi_function_steering_wheel'
    | 'navigation_system'
    | 'parking_sensors'
    | 'passenger_airbags'
    | 'power_assisted_steering'
    | 'power_front_seats'
    | 'power_mirrors'
    | 'power_windows'
    | 'price'
    | 'radio_with_speakers'
    | 'safety_belt_pretensioners'
    | 'side_airbags'
    | 'split_folding_rear_seat'
    | 'sunroof'
    | 'tinted_glass'
    | 'traction_control'
    | 'trip_computer'
    | 'vehicle_body_type'
    | 'vehicle_colour'
    | 'vehicle_doors'
    | 'vehicle_engine_size'
    | 'vehicle_fuel_type'
    | 'vehicle_make'
    | 'vehicle_mileage'
    | 'vehicle_model'
    | 'vehicle_registration_year'
    | 'vehicle_transmission'
    | 'vrn'
    //#region unseen
    | 'vehicle_estimated_mileage'
    | 'support_shipping'
    | 'parcel_size'
    | 'aquarium_type';
//#endregion

export interface AutoRenewFormData {
    selected: boolean;
    hoveredPlan: string | null;
    activatingPlan: string | null;
    nextRenewDate?: number | null;
}

export interface PanelFormData {
    advertCount?: null;
    advertLimit?: null;
    area?: null;
    attributes: Partial<Record<AttributeKey, string | null>>;
    categoryId?: number;
    checkoutVariationId: null;
    contactEmail: string;
    contactName: string;
    contactTelephone: string | null;
    contactUrl: string | null;
    description: string | null;
    emailAddress: string;
    features: Record<
        FeatureType,
        {
            productName: string;
            selected: boolean;
        }
    >;
    autoRenewOptions?: Record<AUTO_RENEW_TYPE, AutoRenewFormData>;
    firstName: string | null;
    formErrors: Record<string, string[]>;
    imageIds: number[];
    lastName: string | null;
    locationId: number;
    mainImageId: number | null;
    optInMarketing: boolean;
    password: null;
    postcode: string | null;
    previousContactEmail: null;
    previousContactName: null;
    removeDraft: null;
    telephoneNumber: number | null;
    termsAgreed: null | boolean;
    title: string | null;
    useEmail: boolean;
    usePhone: boolean;
    useUrl: boolean;
    visibleOnMap: boolean;
    vrmStatus: string;
    websiteUrl: string;
    youtubeLink: string | null;
    inAutobizFlow: boolean;
    delivery?: boolean;
    size?: string;
}

export interface SyiSellerType {
    sellerType: 'private' | 'trade';
    label: 'Seller Type';
    attribute: {
        mandatory: true;
        type: null;
        /** e.g. 'Seller Type' */
        label: string;
        displayLabel: null;
        values: (
            | {
                  value: 'trade';
                  displayValue: 'Trade';
                  selected: false;
                  disabled: false;
              }
            | {
                  value: 'private';
                  displayValue: 'Private';
                  selected: true;
                  disabled: false;
              }
        )[];
        /** e.g. 'seller-type-group' */
        id: string;
        description: null;
        priceSensitive: true;
        selectedValue: {
            present: boolean;
        };
    };
    text: null;
    sellerTypeSelected: boolean;
    popUp: null;
    /** e.g. 'seller-type-group' */
    id: string;
}

export interface PriceGuidance {
    totalAds: number;
    adverts: {
        id: number;
        title: string;
        price: string;
        primaryImageURL: string;
        sellerType: string;
        mileage: string;
    }[];
}

export type AIsuggestType = 'title' | 'img';
